"use client"

import { useContext, useEffect, useState, useRef } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import EmailIcon from "@mui/icons-material/Email"
import WhatsAppIcon from "@mui/icons-material/WhatsApp"
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber"
import { Modal, Button, IconButton, CircularProgress, TextField } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { DotLottieReact } from "@lottiefiles/dotlottie-react"

import { abrirFacturaPdf, generarFacturaPdfBlob } from "../../context/facturaAfipHtmlGenerator.js"
import printerService from "../../context/tickeadora/printerService.js"
import { obtenerParametrosTicket } from "../../context/tickeadora/ticketUtils.js"
import { generateReceiptPDF, generateRefundReceiptPDF } from "../../context/pdf_esqueletos/reciboFacturaCuotaPdf.js"
import { reservePdfTarget } from "../../context/pdf_esqueletos/helpers/pdfDelivery.js"
import Swal from "sweetalert2"

const ModalOpcionesFactura = ({
  open,
  handleClose,
  transaccionData,
  impuestos,
  itemsTransaccion,
  entidadParaFactura,
  usuarioParaFactura,
  tipoDeFactura,
  deDondeViene,
  afipData,
  afipPayload,
  // Nuevas props para recibos de pago
  esReciboPago = false,
  datosRecibo = null,
  // Nuevas props para recibos de devolución
  esReciboDevolucion = false,
  datosDevolucion = null,
}) => {
  const { generarFacturaPDF, generarRemitoPDF, enviarFacturaWhatsApp, enviarMail , guardarComprobante, isNavbarCollapsed, updateEntidadEmail, updateEntidad, getParametrosGlobales,
  } = useContext(ErpContext)
  const [textoDeExito, setTextoDeExito] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [accionActual, setAccionActual] = useState("")
  const [openEmailModal, setOpenEmailModal] = useState(false)
  const [emailValue, setEmailValue] = useState("")
  const [emailError, setEmailError] = useState("")
  const [openPhoneModal, setOpenPhoneModal] = useState(false)
  const [phoneValue, setPhoneValue] = useState("")
  const [phoneError, setPhoneError] = useState("")
  const hasAutoPrintedRef = useRef(false)
  const [usaTickeadoraActiva, setUsaTickeadoraActiva] = useState(null)
  const [parametrosTicket, setParametrosTicket] = useState(null)
  const [logoTenant, setLogoTenant] = useState(null)
  const [showFechaViajePdf, setShowFechaViajePdf] = useState(true)

  useEffect(() => {
    let cancelado = false

    if (!open) {
      setLogoTenant(null)
      setShowFechaViajePdf(true)
      return
    }

    ;(async () => {
      try {
        const params = await getParametrosGlobales([
          'logoNavbarTenant',
          'verFechaSalidaYFechaFinPdf',
          'verFechaSalidaPdf',
        ])
        const fechaViajePdfConfig =
          params?.verFechaSalidaYFechaFinPdf ?? params?.verFechaSalidaPdf
        if (cancelado) return
        setLogoTenant(params?.logoNavbarTenant || null)
        setShowFechaViajePdf(
          fechaViajePdfConfig === 1 ||
          fechaViajePdfConfig === "1" ||
          fechaViajePdfConfig === true ||
          fechaViajePdfConfig === "true"
        )
      } catch (error) {
        if (!cancelado) {
          console.error("Error cargando logo del tenant:", error)
          setLogoTenant(null)
          setShowFechaViajePdf(true)
        }
      }
    })()

    return () => {
      cancelado = true
    }
  }, [open, getParametrosGlobales])

  useEffect(() => {
    let cancelado = false

    if (!open) {
      hasAutoPrintedRef.current = false
      setUsaTickeadoraActiva(null)
      setParametrosTicket(null)
      return
    }

    ;(async () => {
      try {
        const params = await obtenerParametrosTicket(getParametrosGlobales)
        if (cancelado) return
        setParametrosTicket(params)
        const usaTickeadora = params?.usaTickeadora
        const habilitado = usaTickeadora === 1 || usaTickeadora === '1' || usaTickeadora === true
        setUsaTickeadoraActiva(habilitado)
      } catch (error) {
        if (!cancelado) {
          console.error('Error cargando configuración de tickeadora:', error)
          setUsaTickeadoraActiva(false)
          setParametrosTicket(null)
        }
      }
    })()

    return () => {
      cancelado = true
    }
  }, [open, getParametrosGlobales])

  useEffect(() => {
    if (deDondeViene == "venta") {
      setTextoDeExito("¡Tu VENTA se realizó con éxito!")
    }
    if (deDondeViene == "compra") {
      setTextoDeExito("¡Tu COMPRA se realizó con éxito!")
    }
    if (deDondeViene == "presupuesto") {
      setTextoDeExito("¡Tu PRESUPUESTO se realizó con éxito!")
    }
    if (deDondeViene == "devolucion" && esReciboDevolucion) {
      setTextoDeExito("¡Pago REVERTIDO exitosamente!")
    } else if (deDondeViene == "devolucion") {
      setTextoDeExito("¡Tu DEVOLUCIÓN se realizó con éxito!")
    }
    if (deDondeViene == "pago") {
      setTextoDeExito("¡Tu PAGO se realizó con éxito!")
    }
  }, [deDondeViene, esReciboDevolucion])

  useEffect(() => {
    if (open && deDondeViene === 'venta' && usaTickeadoraActiva === true && !hasAutoPrintedRef.current) {
      hasAutoPrintedRef.current = true
      handlePrintTicket()
    }

    if (!open || usaTickeadoraActiva === false) {
      hasAutoPrintedRef.current = false
    }
  }, [open, deDondeViene, usaTickeadoraActiva])

  const handleDownloadPdf = async () => {
    const deliveryContext = { targetWindow: reservePdfTarget() }
    try {
      setIsLoading(true)
      setAccionActual("Generando PDF...")

      // Si es recibo de pago, usar generateReceiptPDF
      if (deDondeViene === "pago" && esReciboPago && datosRecibo) {
        const { cuota, cuentaCorriente, cuotasInfo } = datosRecibo
        generateReceiptPDF(cuota, cuota.transaccion, cuentaCorriente, cuotasInfo, false, logoTenant, deliveryContext, showFechaViajePdf)
        setIsLoading(false)
        setAccionActual("")
        return
      }

      // Si es recibo de devolución, usar generateRefundReceiptPDF
      if (deDondeViene === "devolucion" && esReciboDevolucion && datosDevolucion) {
        const { cuota, datosReversion, cuentaCorriente, cuotasInfo } = datosDevolucion
        generateRefundReceiptPDF(cuota, datosReversion, cuentaCorriente, cuotasInfo, false, logoTenant, deliveryContext, showFechaViajePdf)
        setIsLoading(false)
        setAccionActual("")
        return
      }

      if (deDondeViene === "devolucion") {
        // ✅ NUEVA LÓGICA PARA DEVOLUCIONES
        // Generar PDF local para devoluciones
        const pdfDataUrl = await generarFacturaPDF(
          transaccionData, 
          itemsTransaccion, 
          entidadParaFactura, 
          true, // tipoDeFactura = true (local)
          impuestos, 
          deDondeViene
        )
        const finalPdfUrl = pdfDataUrl?.data?.pdfUrl || pdfDataUrl
        
        // Guardar en carpeta devoluciones
        await guardarComprobante(transaccionData.id, finalPdfUrl, 'devolucion')
      } else if (deDondeViene === "venta" && tipoDeFactura === false && afipData && afipPayload) {
        // Factura en blanco (AFIP)
        const blob = await generarFacturaPdfBlob(afipData, afipPayload, getParametrosGlobales)
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
        const tipoEnvio = deDondeViene === 'presupuesto' ? 'presupuesto' : 'factura'
        await guardarComprobante(transaccionData.id, dataUrl, tipoEnvio)
        await abrirFacturaPdf(afipData, afipPayload, getParametrosGlobales, deliveryContext)
      } else {
        // Factura en negro (local)
        const pdfDataUrl = await generarFacturaPDF(transaccionData, itemsTransaccion, entidadParaFactura, tipoDeFactura, impuestos, deDondeViene)
        const tipoEnvio = deDondeViene === 'presupuesto' ? 'presupuesto' : 'factura'
        await guardarComprobante(transaccionData.id, (pdfDataUrl?.data?.pdfUrl || pdfDataUrl), tipoEnvio)
      }
    } catch (error) {
      console.error("Error al generar la factura PDF:", error)
    } finally {
      setIsLoading(false)
      setAccionActual("")
    }
  }

  const handleDownloadRemito = async () => {
    const remitoDelivery = { targetWindow: reservePdfTarget() }
    try {
      setIsLoading(true)
      setAccionActual("Generando remito...")
      const remitoDataUrl = await generarRemitoPDF(
        transaccionData,
        itemsTransaccion,
        entidadParaFactura,
        usuarioParaFactura,
        tipoDeFactura,
        impuestos,
        remitoDelivery,
      )
      await guardarComprobante(transaccionData.id, (remitoDataUrl?.data?.pdfUrl || remitoDataUrl), 'remito')
    } catch (error) {
      console.error("Error al generar el remito PDF:", error)
    } finally {
      setIsLoading(false)
      setAccionActual("")
    }
  }

  const handlePrintTicket = async () => {
    try {
      if (usaTickeadoraActiva === false) {
        return
      }

      setIsLoading(true)
      setAccionActual("Preparando impresión...")
      
      // Obtener parámetros globales necesarios para el ticket
      let parametrosGlobales = parametrosTicket
      if (!parametrosGlobales) {
        parametrosGlobales = await obtenerParametrosTicket(getParametrosGlobales)
        setParametrosTicket(parametrosGlobales)
        const usaTickeadora = parametrosGlobales?.usaTickeadora
        if (usaTickeadoraActiva === null) {
          const habilitado = usaTickeadora === 1 || usaTickeadora === '1' || usaTickeadora === true
          setUsaTickeadoraActiva(habilitado)
          if (!habilitado) {
            setIsLoading(false)
            setAccionActual("")
            return
          }
        }
      }

      setAccionActual("Imprimiendo ticket...")
      
      // Imprimir ticket usando el nuevo servicio de impresión directa
      const result = await printerService.printTicket({
        transaccionData,
        itemsTransaccion,
        entidadParaFactura,
        parametrosGlobales
      })
      
      if (result.success) {
        setTextoDeExito("Ticket impreso correctamente")
      } else if (result.message) {
        const errorMsg = result.message
        setTextoDeExito(`Error: ${errorMsg}`)
      }

      if (result.success || result.message) {
        setTimeout(() => {
          setTextoDeExito("")
        }, 5000)
      }
      
    } catch (error) {
      console.error("Error al imprimir ticket:", error)
      setTextoDeExito(`Error: ${error.message}`)
      setTimeout(() => {
        setTextoDeExito("")
      }, 5000)
    } finally {
      setIsLoading(false)
      setAccionActual("")
    }
  }

  const openEmailPrompt = () => {
    // Prefill from entidad email except if generic ids 1 or 2
    const idEnt = entidadParaFactura?.id
    const email = entidadParaFactura?.email
    
    // Validar si el email es NULL o vacío
    if (!email || email.trim() === "") {
      Swal.fire({
        icon: "warning",
        title: "Email no registrado",
        text: "La entidad no tiene un email registrado. Por favor, ingrese el email del cliente para continuar.",
        confirmButtonText: "Agregar Email",
        showCancelButton: true,
        cancelButtonText: "Cancelar",
        customClass: {
          container: 'swal-high-z-index'
        },
        didOpen: () => {
          // Asegurar que SweetAlert aparezca sobre el modal
          const swalContainer = document.querySelector('.swal2-container');
          if (swalContainer) {
            swalContainer.style.zIndex = '10000';
          }
        }
      }).then((result) => {
        // Si el usuario confirma, abrir el modal de captura
        if (result.isConfirmed) {
          setEmailValue("") // Limpiar campo
          setEmailError("")
          setOpenEmailModal(true) // Abrir modal para capturar email
        }
      })
      return
    }
    
    const prefill = idEnt === 1 || idEnt === 2 ? "" : email
    setEmailValue(prefill)
    setEmailError("")
    setOpenEmailModal(true)
  }

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }
  const validatePhone = (phone) => /^\+?\d{7,15}$/.test((phone || '').trim())

  const handleWhatsAppSend = async () => {
    try {
      setPhoneError("")
      if (!validatePhone(phoneValue)) {
        setPhoneError('Teléfono inválido (formato +549...)')
        return
      }
      setIsLoading(true)
      setAccionActual("Enviando WhatsApp...")

      // Generar el PDF como dataURL (igual que en Email)
      let pdfDataUrl
      let tipoEnvio = 'factura' // Usar 'factura' para no romper backend

      // Si es recibo de pago
      if (deDondeViene === "pago" && esReciboPago && datosRecibo) {
        const { cuota, cuentaCorriente, cuotasInfo } = datosRecibo
        pdfDataUrl = generateReceiptPDF(cuota, cuota.transaccion, cuentaCorriente, cuotasInfo, true, logoTenant, null, showFechaViajePdf)
        tipoEnvio = 'factura' // Mantener como 'factura'
      } else if (deDondeViene === "devolucion" && esReciboDevolucion && datosDevolucion) {
        // Si es recibo de devolución
        const { cuota, datosReversion, cuentaCorriente, cuotasInfo } = datosDevolucion
        pdfDataUrl = generateRefundReceiptPDF(cuota, datosReversion, cuentaCorriente, cuotasInfo, true, logoTenant, null, showFechaViajePdf)
        tipoEnvio = 'factura' // Mantener como 'factura'
      } else if (deDondeViene === "devolucion") {
        // ✅ Para devoluciones, siempre PDF local
        const out = await generarFacturaPDF(
          transaccionData,
          itemsTransaccion,
          entidadParaFactura,
          true, // tipoDeFactura = true (local)
          impuestos,
          deDondeViene
        )
        pdfDataUrl = out?.data?.pdfUrl || out
      } else if (deDondeViene === "venta" && tipoDeFactura === false && afipData && afipPayload) {
        const blob = await generarFacturaPdfBlob(afipData, afipPayload, getParametrosGlobales)
        pdfDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
        tipoEnvio = 'factura'
      } else {
        const out = await generarFacturaPDF(
          transaccionData,
          itemsTransaccion,
          entidadParaFactura,
          tipoDeFactura,
          impuestos,
          deDondeViene,
          true
        )
        pdfDataUrl = out?.data?.pdfUrl || out
        tipoEnvio = deDondeViene === 'presupuesto' ? 'presupuesto' : 
                   deDondeViene === 'devolucion' ? 'devolucion' : 'factura'
      }
      const result = await enviarFacturaWhatsApp(
        transaccionData.id,
        pdfDataUrl,
        phoneValue,
        tipoEnvio
      )
      setOpenPhoneModal(false)

      if (result?.waLink) {
        window.open(result.waLink, '_blank', 'noopener,noreferrer')
      }
    } catch (error) {
      console.error("Error al enviar la factura por WhatsApp:", error)
    } finally {
      setIsLoading(false)
      setAccionActual("")
    }
  }

  const handleEmailSend = async () => {
    try {
      setEmailError("")
      if (!emailValue || !validateEmail(emailValue)) {
        setEmailError("Email inválido")
        return
      }
      setIsLoading(true)
      setAccionActual("Enviando email...")
      let pdfDataUrl
      let tipoEnvio = 'factura' // Usar 'factura' para no romper backend

      // Si es recibo de pago
      if (deDondeViene === "pago" && esReciboPago && datosRecibo) {
        const { cuota, cuentaCorriente, cuotasInfo } = datosRecibo
        pdfDataUrl = generateReceiptPDF(cuota, cuota.transaccion, cuentaCorriente, cuotasInfo, true, logoTenant, null, showFechaViajePdf)
        tipoEnvio = 'factura' // Mantener como 'factura' para no modificar backend
      } else if (deDondeViene === "devolucion" && esReciboDevolucion && datosDevolucion) {
        // Si es recibo de devolución de cuota
        const { cuota, datosReversion, cuentaCorriente, cuotasInfo } = datosDevolucion
        pdfDataUrl = generateRefundReceiptPDF(cuota, datosReversion, cuentaCorriente, cuotasInfo, true, logoTenant, null, showFechaViajePdf)
        tipoEnvio = 'factura' // Mantener como 'factura'
      } else if (deDondeViene === "devolucion") {
        // ✅ Para devoluciones, siempre PDF local
        pdfDataUrl = await generarFacturaPDF(
          transaccionData,
          itemsTransaccion,
          entidadParaFactura,
          true, // tipoDeFactura = true (local)
          impuestos,
          deDondeViene,
          true
        )
      } else if (deDondeViene === "venta" && tipoDeFactura === false && afipData && afipPayload) {
        // Generar PDF desde AFIP (blob -> dataURL)
        const blob = await generarFacturaPdfBlob(afipData, afipPayload, getParametrosGlobales)
        pdfDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
        tipoEnvio = 'factura'
      } else {
        // Generar PDF local como dataURI, respetando el origen (venta/presupuesto/etc.)
        pdfDataUrl = await generarFacturaPDF(
          transaccionData,
          itemsTransaccion,
          entidadParaFactura,
          tipoDeFactura,
          impuestos,
          deDondeViene,
          true
        )
        tipoEnvio = deDondeViene === 'presupuesto' ? 'presupuesto' : 
                   deDondeViene === 'devolucion' ? 'devolucion' : 'factura'
      }
      console.log(`[ModalFactura] pdfBase64 prefix: ${typeof pdfDataUrl === 'string' ? pdfDataUrl.slice(0, 40) : typeof pdfDataUrl}`)
      console.log(`[ModalFactura] pdfBase64 length: ${pdfDataUrl?.length || 0}`)
      await enviarMail(transaccionData.id, pdfDataUrl, emailValue, tipoEnvio)
      setOpenEmailModal(false)
    } catch (error) {
      console.error("Error enviando email:", error)
    } finally {
      setIsLoading(false)
      setAccionActual("")
    }
  }

  const handleActualizarEmailCliente = async () => {
    try {
      setEmailError("")
      const idEnt = entidadParaFactura?.id
      if (!idEnt || idEnt === 1 || idEnt === 2) {
        setEmailError("No se puede actualizar email para entidad genérica")
        return
      }
      if (!emailValue || !validateEmail(emailValue)) {
        setEmailError("Email inválido")
        return
      }
      setIsLoading(true)
      setAccionActual("Actualizando email del cliente...")
      await updateEntidadEmail(idEnt, emailValue)
      
      // Actualizar localmente para que el modal lo reconozca
      if (entidadParaFactura) {
        entidadParaFactura.email = emailValue
      }
      
      setAccionActual("")
      
      // Notificación de éxito
      Swal.fire({
        icon: "success",
        title: "Email actualizado",
        text: "El email del cliente se actualizó correctamente.",
        timer: 2000,
        showConfirmButton: false,
        customClass: {
          container: 'swal-high-z-index'
        },
        didOpen: () => {
          const swalContainer = document.querySelector('.swal2-container');
          if (swalContainer) {
            swalContainer.style.zIndex = '10000';
          }
        }
      })
    } catch (error) {
      console.error("Error actualizando email del cliente:", error)
      setEmailError("No se pudo actualizar el email del cliente")
      
      // Notificación de error
      Swal.fire({
        icon: "error",
        title: "Error al actualizar",
        text: "No se pudo actualizar el email del cliente. Por favor, intente nuevamente.",
        confirmButtonText: "Entendido",
        customClass: {
          container: 'swal-high-z-index'
        },
        didOpen: () => {
          const swalContainer = document.querySelector('.swal2-container');
          if (swalContainer) {
            swalContainer.style.zIndex = '10000';
          }
        }
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleActualizarTelefonoCliente = async () => {
    try {
      setPhoneError("")
      const idEnt = entidadParaFactura?.id
      if (!idEnt || idEnt === 1 || idEnt === 2) {
        setPhoneError("No se puede actualizar teléfono para entidad genérica")
        return
      }
      if (!phoneValue || !validatePhone(phoneValue)) {
        setPhoneError("Teléfono inválido (formato +549...)")
        return
      }
      setIsLoading(true)
      setAccionActual("Actualizando teléfono del cliente...")
      
      // Actualizar usando updateEntidad con todos los datos actuales
      await updateEntidad(
        idEnt,
        entidadParaFactura.descripcion,
        entidadParaFactura.apellido,
        phoneValue, // Nuevo teléfono
        entidadParaFactura.email,
        entidadParaFactura.dniCuitCuil,
        entidadParaFactura.direccion,
        entidadParaFactura.localidad,
        entidadParaFactura.provincia,
        entidadParaFactura.idTipoEntidad,
        entidadParaFactura.idCondicionIva,
        entidadParaFactura.sexo,
        entidadParaFactura.fechaNacimiento,
        entidadParaFactura.pais,
        entidadParaFactura.codigoPostal,
        entidadParaFactura.observaciones,
        entidadParaFactura.esTutor,
        entidadParaFactura.nombreTutor,
        entidadParaFactura.apellidoTutor
      )
      
      // Actualizar localmente para que el modal lo reconozca
      if (entidadParaFactura) {
        entidadParaFactura.telefono = phoneValue
      }
      
      setAccionActual("")
      
      // Notificación de éxito
      Swal.fire({
        icon: "success",
        title: "Teléfono actualizado",
        text: "El teléfono del cliente se actualizó correctamente.",
        timer: 2000,
        showConfirmButton: false,
        customClass: {
          container: 'swal-high-z-index'
        },
        didOpen: () => {
          const swalContainer = document.querySelector('.swal2-container');
          if (swalContainer) {
            swalContainer.style.zIndex = '10000';
          }
        }
      })
    } catch (error) {
      console.error("Error actualizando teléfono del cliente:", error)
      setPhoneError("No se pudo actualizar el teléfono del cliente")
      
      // Notificación de error
      Swal.fire({
        icon: "error",
        title: "Error al actualizar",
        text: "No se pudo actualizar el teléfono del cliente. Por favor, intente nuevamente.",
        confirmButtonText: "Entendido",
        customClass: {
          container: 'swal-high-z-index'
        },
        didOpen: () => {
          const swalContainer = document.querySelector('.swal2-container');
          if (swalContainer) {
            swalContainer.style.zIndex = '10000';
          }
        }
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleWhatsApp = async () => {
    try {
      setIsLoading(true)
      setAccionActual("Enviando WhatsApp...")

      // Generar el PDF como dataURL (igual que en Email)
      let pdfDataUrl
      let tipoEnvio = 'devolucion' // ✅ Nuevo tipo por defecto

      // Si es recibo de devolución de cuota
      if (deDondeViene === "devolucion" && esReciboDevolucion && datosDevolucion) {
        const { cuota, datosReversion, cuentaCorriente, cuotasInfo } = datosDevolucion
        pdfDataUrl = generateRefundReceiptPDF(cuota, datosReversion, cuentaCorriente, cuotasInfo, true, logoTenant, null, showFechaViajePdf)
        tipoEnvio = 'factura' // Mantener como 'factura'
      } else if (deDondeViene === "devolucion") {
        // ✅ Para devoluciones, siempre PDF local
        pdfDataUrl = await generarFacturaPDF(
          transaccionData,
          itemsTransaccion,
          entidadParaFactura,
          true, // tipoDeFactura = true (local)
          impuestos,
          deDondeViene
        )
        pdfDataUrl = pdfDataUrl?.data?.pdfUrl || pdfDataUrl
      } else if (deDondeViene === "venta" && tipoDeFactura === false && afipData && afipPayload) {
        const blob = await generarFacturaPdfBlob(afipData, afipPayload, getParametrosGlobales)
        pdfDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
        tipoEnvio = 'factura'
      } else {
        pdfDataUrl = await generarFacturaPDF(
          transaccionData,
          itemsTransaccion,
          entidadParaFactura,
          tipoDeFactura,
          impuestos,
          deDondeViene,
          true
        )
        pdfDataUrl = pdfDataUrl?.data?.pdfUrl || pdfDataUrl
         tipoEnvio = deDondeViene === 'presupuesto' ? 'presupuesto' : 
                   deDondeViene === 'devolucion' ? 'devolucion' : 'factura'
      }

     const phone = entidadParaFactura?.telefono || ""

      const result = await enviarFacturaWhatsApp(
        transaccionData.id,
        pdfDataUrl,
        phone,
        tipoEnvio
      )

      // Abrimos el chat de WhatsApp del usuario con el mensaje prellenado
      if (result?.waLink) {
        window.open(result.waLink, '_blank', 'noopener,noreferrer')
      }
    } catch (error) {
      console.error("Error al enviar la factura por WhatsApp:", error)
    } finally {
      setIsLoading(false)
      setAccionActual("")
    }
  }

  return (
    <>
    <Modal open={open} onClose={handleClose}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 16,
          padding: 30,
          backgroundColor: "white",
          margin: "auto",
          marginTop: "10%",
          width: "60%",
          maxWidth: 800,
          boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.12)",
          position: "relative",
        }}
      >
        {/* Botón para cerrar el modal */}
        <IconButton
          className="colorGeneralParaIconos"
          onClick={handleClose}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "#757575",
            backgroundColor: "#f5f5f5",
            boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <CloseIcon />
        </IconButton>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            position: "relative",
          }}
        >
          <h1
            style={{
              marginBottom: 20,
              color: "#4CAF50",
              fontSize: 32,
              fontWeight: 600,
              textAlign: "center",
            }}
          >
            {textoDeExito}
          </h1>

          <DotLottieReact
            src="https://lottie.host/3bf4b8df-b7cf-4d60-838a-ad9013672347/bpnBcXsF0f.lottie"
            loop={false}
            autoplay
            style={{ width: 240, marginBottom: 30 }}
          />

          {/* Mensaje de advertencia para recibo de devolución */}
          {esReciboDevolucion && (
            <div
              style={{
                width: "100%",
                padding: 16,
                marginBottom: 20,
                borderRadius: 8,
                backgroundColor: "#fff3cd",
                border: "1px solid #ffc107",
                textAlign: "center",
              }}
            >
              <p
                style={{
                  margin: 0,
                  color: "#856404",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                ⚠️ Este es el único momento para descargar/enviar el recibo de devolución
              </p>
              <p
                style={{
                  margin: "8px 0 0 0",
                  color: "#856404",
                  fontSize: 13,
                }}
              >
                El recibo no se guardará y solo estará disponible ahora
              </p>
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 24,
              width: "100%",
              flexDirection: window.innerWidth < 768 ? "column" : "row",
            }}
          >
            {/* Opciones de Descarga */}
            <div
              style={{
                flex: 1,
                padding: 24,
                borderRadius: 12,
                backgroundColor: "#f9f9f9",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #eaeaea",
              }}
            >
              <h2
                style={{
                  marginBottom: 20,
                  color: "#333",
                  fontSize: 20,
                  fontWeight: 500,
                  borderBottom: "2px solid #e0e0e0",
                  paddingBottom: 10,
                }}
              >
              {esReciboDevolucion ? "Recibo de Devolución" : "Opciones"}
              </h2>

              {usaTickeadoraActiva && deDondeViene !== "pago" && !esReciboDevolucion && (
                <Button
                  onClick={handlePrintTicket}
                  variant="contained"
                  color="success"
                  startIcon={<ConfirmationNumberIcon />}
                  style={{
                    marginBottom: 16,
                    padding: "12px 16px",
                    borderRadius: 8,
                    fontWeight: 500,
                    boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
                  }}
                  disabled={isLoading}
                >
                  Imprimir Ticket
                </Button>
              )}

              <Button
                onClick={handleDownloadPdf}
                variant="contained"
                color="primary"
                startIcon={<PictureAsPdfIcon />}
                style={{
                  marginBottom: 16,
                  padding: "12px 16px",
                  borderRadius: 8,
                  fontWeight: 500,
                  boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
                }}
                disabled={isLoading}
              >
                {esReciboDevolucion ? "Descargar Recibo de Devolución" : "Descargar PDF"}
              </Button>

              {!esReciboDevolucion && (deDondeViene !== "compra" && deDondeViene !== "pago" ? (
                <Button
                  onClick={handleDownloadRemito}
                  variant="contained"
                  color="primary"
                  startIcon={<ReceiptLongIcon />}
                  style={{
                    padding: "12px 16px",
                    borderRadius: 8,
                    fontWeight: 500,
                    boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
                  }}
                  disabled={isLoading}
                >
                  Descargar Remito
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  disabled
                  startIcon={<ReceiptLongIcon />}
                  style={{
                    padding: "12px 16px",
                    borderRadius: 8,
                    opacity: 0.7,
                  }}
                >
                  Descargar Remito
                </Button>
              ))}
            </div>

            {/* Opciones de Envío */}
            <div
              style={{
                flex: 1,
                padding: 24,
                borderRadius: 12,
                backgroundColor: "#f9f9f9",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #eaeaea",
              }}
            >
              <h2
                style={{
                  marginBottom: 20,
                  color: "#333",
                  fontSize: 20,
                  fontWeight: 500,
                  borderBottom: "2px solid #e0e0e0",
                  paddingBottom: 10,
                }}
              >
                Opciones de envío
              </h2>

              <Button
                onClick={openEmailPrompt}
                variant="outlined"
                startIcon={<EmailIcon />}
                style={{
                  marginBottom: 16,
                  padding: "12px 16px",
                  borderRadius: 8,
                  fontWeight: 500,
                  borderWidth: 2,
                }}
                disabled={isLoading}
              >
                Enviar por Email
              </Button>

              <Button
                onClick={() => {
                  const idEnt = entidadParaFactura?.id
                  const telefono = entidadParaFactura?.telefono
                  
                  // Validar si el teléfono es NULL o vacío
                  if (!telefono || telefono.trim() === "") {
                    Swal.fire({
                      icon: "warning",
                      title: "Teléfono no registrado",
                      text: "La entidad no tiene un número de teléfono registrado. Por favor, ingrese el teléfono del cliente para continuar.",
                      confirmButtonText: "Agregar Teléfono",
                      showCancelButton: true,
                      cancelButtonText: "Cancelar",
                      customClass: {
                        container: 'swal-high-z-index'
                      },
                      didOpen: () => {
                        // Asegurar que SweetAlert aparezca sobre el modal
                        const swalContainer = document.querySelector('.swal2-container');
                        if (swalContainer) {
                          swalContainer.style.zIndex = '10000';
                        }
                      }
                    }).then((result) => {
                      // Si el usuario confirma, abrir el modal de captura
                      if (result.isConfirmed) {
                        setPhoneValue("") // Limpiar campo
                        setPhoneError('')
                        setOpenPhoneModal(true) // Abrir modal para capturar teléfono
                      }
                    })
                    return
                  }
                  
                  const prefill = idEnt === 1 ? '' : telefono
                  setPhoneValue(prefill)
                  setPhoneError('')
                  setOpenPhoneModal(true)
                }}
                variant="outlined"
                startIcon={<WhatsAppIcon style={{ color: "#25D366" }} />}
                style={{
                  padding: "12px 16px",
                  borderRadius: 8,
                  fontWeight: 500,
                  borderWidth: 2,
                }}
                disabled={isLoading}
              >
                Enviar por WhatsApp
              </Button>
            </div>
          </div>

          {isLoading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 10,
                borderRadius: 16,
              }}
            >
              <CircularProgress size={60} thickness={4} />
              <p
                style={{
                  marginTop: 16,
                  fontSize: 18,
                  fontWeight: 500,
                  color: "#333",
                }}
              >
                {accionActual}
              </p>
            </div>
          )}
        </div>
      </div>
    </Modal>

    {/* Modal para ingresar teléfono destino (WhatsApp) */}
    <Modal open={openPhoneModal} onClose={() => setOpenPhoneModal(false)}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          borderRadius: 12,
          padding: 20,
          backgroundColor: "white",
          margin: "auto",
          marginTop: "15%",
          width: 420,
          boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.12)",
          position: "relative",
        }}
      >
        <h3 style={{ margin: 0 }}>Enviar por WhatsApp</h3>
        <TextField
          label="Teléfono del cliente (ej: +549...)"
          value={phoneValue}
          onChange={(e) => setPhoneValue(e.target.value)}
          error={!!phoneError}
          helperText={phoneError || ""}
          size="small"
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button variant="outlined" onClick={handleActualizarTelefonoCliente} disabled={isLoading}>
            Actualizar teléfono cliente
          </Button>
          <Button variant="contained" onClick={handleWhatsAppSend} disabled={isLoading}>
            Enviar
          </Button>
        </div>
      </div>
    </Modal>

    {/* Modal pequeño para ingresar email destino */}
    <Modal open={openEmailModal} onClose={() => setOpenEmailModal(false)}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          borderRadius: 12,
          padding: 20,
          backgroundColor: "white",
          margin: "auto",
          marginTop: "15%",
          width: 420,
          boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.12)",
          position: "relative",
        }}
      >
        <h3 style={{ margin: 0 }}>Enviar factura por email</h3>
        <TextField
          label="Email del cliente"
          value={emailValue}
          onChange={(e) => setEmailValue(e.target.value)}
          error={!!emailError}
          helperText={emailError || ""}
          size="small"
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button variant="outlined" onClick={handleActualizarEmailCliente} disabled={isLoading}>
            Actualizar email cliente
          </Button>
          <Button variant="contained" onClick={handleEmailSend} disabled={isLoading}>
            Enviar
          </Button>
        </div>
      </div>
    </Modal>
    </>
  )
}

export default ModalOpcionesFactura
